// src/components/brand/brand-switcher-modal.tsx
import { useEffect, useMemo, useState, useCallback } from "react";
import {
  Building2,
  Search,
  Check,
  X,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { getMyBrands, switchActiveBrand } from "@/app/services/brand/brand-access-service";

interface BrandSwitcherModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * Called after the active brand has been switched server-side.
   * App.tsx uses this to reconfigure the session (load Brand
   * Monitoring for the new brand) and route to the dashboard.
   */
  onBrandSwitched: (brand: string) => void;
}

/**
 * Modal launched from the bottom of the left navigation. Lists only
 * the brands assigned to the current user and lets them switch the
 * active one. Self-fetches so it always shows a fresh list.
 */
export function BrandSwitcherModal({
  open,
  onClose,
  onBrandSwitched,
}: BrandSwitcherModalProps) {
  const [loading, setLoading] = useState(false);
  const [switching, setSwitching] = useState<string | null>(null);
  const [err, setErr] = useState("");
  const [brands, setBrands] = useState<string[]>([]);
  const [runCounts, setRunCounts] = useState<Record<string, number>>({});
  const [activeBrand, setActiveBrand] = useState<string | null>(null);
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const res = await getMyBrands();
      setBrands(res.brands);
      setActiveBrand(res.active_brand);
      const counts: Record<string, number> = {};
      for (const s of res.brand_summaries || []) counts[s.brand_name] = s.run_count;
      setRunCounts(counts);
    } catch (e: any) {
      setErr(e?.message || "Couldn't load your brands.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setQuery("");
      load();
    }
  }, [open, load]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return brands;
    return brands.filter((b) => b.toLowerCase().includes(q));
  }, [brands, query]);

  const handlePick = async (brand: string) => {
    // Brands with no monitoring run yet aren't loadable.
    if ((runCounts[brand] ?? 0) <= 0) return;
    if (brand === activeBrand) {
      onClose();
      return;
    }
    setSwitching(brand);
    setErr("");
    try {
      const res = await switchActiveBrand(brand);
      setActiveBrand(res.active_brand);
      onBrandSwitched(res.active_brand || brand);
      onClose();
    } catch (e: any) {
      setErr(e?.message || "Couldn't switch brand.");
    } finally {
      setSwitching(null);
    }
  };

  if (!open) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 60,
        backgroundColor: "rgba(0,0,0,0.45)",
        backdropFilter: "blur(2px)",
        WebkitBackdropFilter: "blur(2px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "clamp(16px, 4vw, 28px)",
        animation: "fadeIn 0.18s ease-out",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 460,
          maxHeight: "min(78vh, 620px)",
          display: "flex",
          flexDirection: "column",
          backgroundColor: "var(--card)",
          border: "1px solid var(--border)",
          borderRadius: 16,
          boxShadow: "var(--elevation-lg)",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "18px 20px",
            borderBottom: "1px solid var(--border)",
          }}
        >
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "var(--signal-blue-10)",
              border: "1px solid var(--signal-blue-25)",
              flexShrink: 0,
            }}
          >
            <Building2 style={{ width: 18, height: 18, color: "var(--signal-blue)" }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontFamily: "var(--font-sans)",
                fontSize: "var(--text-base)",
                fontWeight: "var(--font-weight-semibold)",
                color: "var(--foreground)",
                lineHeight: 1.2,
              }}
            >
              Switch brand
            </div>
            <div
              style={{
                fontSize: "var(--text-xs)",
                color: "var(--muted-foreground)",
                marginTop: 2,
              }}
            >
              Choose which brand to work in
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              padding: 6,
              border: "none",
              background: "transparent",
              cursor: "pointer",
              color: "var(--muted-foreground)",
              display: "flex",
              alignItems: "center",
            }}
          >
            <X style={{ width: 18, height: 18 }} />
          </button>
        </div>

        {/* Search */}
        <div style={{ padding: "14px 20px 10px" }}>
          <div style={{ position: "relative" }}>
            <Search
              style={{
                position: "absolute",
                left: 12,
                top: "50%",
                transform: "translateY(-50%)",
                width: 15,
                height: 15,
                color: "var(--muted-foreground)",
                pointerEvents: "none",
              }}
            />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search your brands…"
              autoFocus
              style={{
                width: "100%",
                padding: "10px 12px 10px 36px",
                borderRadius: 9,
                border: "1px solid var(--border)",
                backgroundColor: "var(--input-background)",
                fontSize: "var(--text-sm)",
                outline: "none",
                boxSizing: "border-box",
                color: "var(--foreground)",
              }}
            />
          </div>
        </div>

        {err && (
          <div
            style={{
              margin: "0 20px 10px",
              padding: "10px 12px",
              borderRadius: "var(--radius)",
              backgroundColor: "var(--infrared-coral-5)",
              border: "1px solid var(--infrared-coral)",
              color: "var(--infrared-coral)",
              fontSize: "var(--text-sm)",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <AlertCircle style={{ width: 15, height: 15, flexShrink: 0 }} />
            {err}
          </div>
        )}

        {/* List */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "4px 12px 16px",
          }}
        >
          {loading ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
                padding: "32px 0",
                color: "var(--muted-foreground)",
                fontSize: "var(--text-sm)",
              }}
            >
              <Loader2 className="animate-spin" style={{ width: 18, height: 18 }} />
              Loading brands…
            </div>
          ) : filtered.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "32px 16px",
                color: "var(--muted-foreground)",
                fontSize: "var(--text-sm)",
              }}
            >
              {brands.length === 0
                ? "No brands assigned to your account."
                : "No brands match your search."}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {filtered.map((brand) => {
                const loadable = (runCounts[brand] ?? 0) > 0;
                const isActive = loadable && brand === activeBrand;
                const isBusy = switching === brand;
                return (
                  <button
                    key={brand}
                    onClick={() => handlePick(brand)}
                    disabled={!loadable || !!switching}
                    aria-disabled={!loadable}
                    title={loadable ? undefined : "No analysis yet for this brand"}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "12px 14px",
                      borderRadius: 10,
                      cursor: loadable
                        ? switching
                          ? "default"
                          : "pointer"
                        : "not-allowed",
                      opacity: loadable ? 1 : 0.55,
                      textAlign: "left",
                      border: `1px solid ${
                        isActive ? "var(--alpha-teal)" : "var(--border)"
                      }`,
                      backgroundColor: isActive
                        ? "var(--alpha-teal-10)"
                        : "var(--card)",
                      transition: "all 0.15s",
                    }}
                  >
                    <div
                      style={{
                        width: 34,
                        height: 34,
                        borderRadius: 9,
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: isActive
                          ? "var(--alpha-teal)"
                          : "var(--muted)",
                        color: isActive ? "white" : "var(--muted-foreground)",
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                    >
                      {brand.trim().slice(0, 2).toUpperCase()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: "var(--text-sm)",
                          fontWeight: isActive ? 600 : 500,
                          color: isActive
                            ? "var(--alpha-teal)"
                            : "var(--foreground)",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {brand}
                      </div>
                      {!loadable && (
                        <div
                          style={{
                            fontSize: "var(--text-xs)",
                            color: "var(--muted-foreground)",
                            marginTop: 2,
                          }}
                        >
                          No analysis yet
                        </div>
                      )}
                    </div>
                    {isBusy ? (
                      <Loader2
                        className="animate-spin"
                        style={{ width: 16, height: 16, color: "var(--alpha-teal)" }}
                      />
                    ) : isActive ? (
                      <Check
                        style={{ width: 16, height: 16, color: "var(--alpha-teal)" }}
                      />
                    ) : null}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}